import { useEffect, useRef, useState } from "react";

import classnames from "classnames";
import { humanize, isNotEmpty, noop } from "neetocist";
import useDebounce from "neetocommons/react-utils/useDebounce";
import { Close } from "neetoicons";
import { Dropdown, Label, Input, Button, Typography } from "neetoui";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FixedSizeGrid as Grid } from "react-window";

import { GRID_PROPERTIES, ICONS_LIST } from "./constants";
import Icon from "./Icon";
import "./icon-picker.scss";
import NoResults from "./NoResults";

const IconPicker = ({
  filled = false,
  label,
  value = "",
  onClick = noop,
  className = "",
  ...otherProps
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [iconList, setIconList] = useState([]);
  const [activeIcon, setActiveIcon] = useState(value);

  const gridRef = useRef(null);

  const debouncedSearchTerm = useDebounce(searchTerm);
  const { t } = useTranslation();

  const isLargerScreen = () => window.innerWidth > 768;

  const handleSearch = term => {
    const searchTerm = term.toLowerCase().trim().replace(/\s+/g, "_");
    const filteredIcons = ICONS_LIST.filter(icon =>
      icon.toLowerCase().includes(searchTerm)
    );

    setIconList(filteredIcons);
    gridRef.current?.scrollToItem({ rowIndex: 0 });
  };

  const handleRemoveClick = () => {
    onClick("");
    setActiveIcon("");
  };

  const handleDropdownClick = () => {
    gridRef.current?.scrollToItem({
      rowIndex:
        iconList.findIndex(icon => icon === activeIcon) /
        GRID_PROPERTIES.columnCount,
    });
    setSearchTerm("");
  };

  const symbolsStyle = { fontVariationSettings: `'FILL' ${filled ? 1 : 0}` };

  useEffect(() => {
    handleSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const renderGridCell = ({ columnIndex, rowIndex, style, data }) => {
    const iconIndex = rowIndex * GRID_PROPERTIES.columnCount + columnIndex;

    const icon = data[iconIndex];

    const handleIconClick = icon => {
      onClick(icon);
      setActiveIcon(icon);
    };

    return icon ? (
      <Icon
        {...{ icon, style, symbolsStyle }}
        active={icon === activeIcon}
        onClick={handleIconClick}
      />
    ) : null;
  };

  return (
    <div className="flex flex-col items-start gap-2">
      {label && <Label>{label}</Label>}
      <Dropdown
        buttonSize="small"
        buttonStyle="secondary"
        closeOnSelect={false}
        dropdownProps={{ className: classnames("h-96", className) }}
        position="bottom-start"
        strategy="fixed"
        buttonProps={{
          "data-testid": "icon-picker-dropdown",
          onClick: handleDropdownClick,
        }}
        label={
          <span className="flex items-center gap-2" style={symbolsStyle}>
            {value && (
              <span className="material-symbols-rounded neeto-ui-text-base mx-auto">
                {value}
              </span>
            )}
            <Typography style="body2">
              {humanize(value) || t("neetoMolecules.iconPicker.dropdown.label")}
            </Typography>
          </span>
        }
        onClose={() => setSearchTerm("")}
        {...otherProps}
      >
        <div className="w-72 flex-grow p-2 md:w-80">
          <div className="mb-1 flex flex-col gap-y-2">
            <Input
              autoFocus
              autoComplete="off"
              data-testid="pick-icon-text-field"
              value={searchTerm}
              placeholder={t("neetoMolecules.common.search", {
                entity: t("neetoMolecules.iconPicker.entity"),
              })}
              suffix={
                <Button
                  icon={Close}
                  size="small"
                  style="link"
                  onClick={() => setSearchTerm("")}
                />
              }
              onChange={e => setSearchTerm(e.target.value)}
            />
            {value && (
              <div className="neeto-ui-bg-white flex justify-center">
                <Button
                  className="text-sm"
                  data-testid="icon-remove-button"
                  icon={Close}
                  label={t("neetoMolecules.iconPicker.dropdown.removeButton")}
                  style="link"
                  onClick={handleRemoveClick}
                />
              </div>
            )}
          </div>
          {isNotEmpty(iconList) ? (
            <Grid
              className="w-full overflow-x-hidden"
              columnCount={GRID_PROPERTIES.columnCount}
              height={GRID_PROPERTIES.gridHeight}
              itemData={iconList}
              ref={gridRef}
              rowHeight={GRID_PROPERTIES.rowHeight}
              width={GRID_PROPERTIES.gridWidth}
              columnWidth={
                isLargerScreen()
                  ? GRID_PROPERTIES.columnWidthDesktop
                  : GRID_PROPERTIES.columnWidthMobile
              }
              rowCount={Math.ceil(
                iconList.length / GRID_PROPERTIES.columnCount
              )}
            >
              {renderGridCell}
            </Grid>
          ) : (
            <NoResults />
          )}
        </div>
      </Dropdown>
    </div>
  );
};

IconPicker.propTypes = {
  /**
   * To specify whether the filled or outlined icons should be shown.
   */
  filled: PropTypes.bool,
  /**
   * The label to be dislayed above the dropdown. It will be hidden if not specified.
   */
  label: PropTypes.string,
  /**
   * The current icon name in `snake_case`.
   */
  value: PropTypes.string,
  /**
   * The function that is triggered when an icon is selected. It returns the icon name in `snake_case`.
   */
  onClick: PropTypes.func,
  /**
   * To provide additional classes to the dropdown
   */
  className: PropTypes.string,
};

export default IconPicker;
